import { motion } from "motion/react";
import { Link2, Megaphone, Newspaper, PenLine } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAccent } from "@/ui";

const EASE = [0.32, 0.72, 0, 1] as const;
const EDGE_BASE_DELAY = 0.55;
const EDGE_STAGGER = 0.16;
const EDGE_DURATION = 0.6;

interface OrbitNode {
  label: string;
  kind: "trusted" | "junk";
  /** Center position, % of the square container. */
  x: number;
  y: number;
  /** Circle diameter in px. */
  size: number;
  icon?: LucideIcon;
}

/**
 * Draw order alternates trusted/junk so the stagger feels lively.
 * Invented example domains — teaching props, not real sites.
 */
const NODES: readonly OrbitNode[] = [
  { label: "daily-news.example", kind: "trusted", x: 20, y: 17, size: 92, icon: Newspaper },
  { label: "dir-spam.example", kind: "junk", x: 12, y: 57, size: 34 },
  { label: "editorial-blog.example", kind: "trusted", x: 81, y: 27, size: 76, icon: PenLine },
  { label: "free-links.example", kind: "junk", x: 51, y: 9, size: 30 },
  { label: "industry-week.example", kind: "trusted", x: 75, y: 80, size: 68, icon: Megaphone },
  { label: "linkfarm.example", kind: "junk", x: 30, y: 87, size: 32 },
  { label: "web-dir.example", kind: "junk", x: 90, y: 55, size: 28 },
  { label: "link-swap.example", kind: "junk", x: 10, y: 36, size: 26 },
];

interface LinkGraphProps {
  active: boolean;
}

/**
 * Animated backlink graph: yoursite.example at the center, edges drawing in
 * one by one from trusted (bright, thick, accent gradient) and junk
 * (thin, dim grey) referring sites.
 */
export function LinkGraph({ active }: LinkGraphProps) {
  const [from, to] = useAccent();

  return (
    <div className="relative mx-auto aspect-square w-full max-w-[620px]">
      {/* Edges */}
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            id="c3-edge-gradient"
            x1="0"
            y1="0"
            x2="100"
            y2="100"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={from} />
            <stop offset="100%" stopColor={to} />
          </linearGradient>
        </defs>
        {NODES.map((n, i) => {
          const trusted = n.kind === "trusted";
          return (
            <motion.line
              key={n.label}
              x1={50}
              y1={50}
              x2={n.x}
              y2={n.y}
              stroke={trusted ? "url(#c3-edge-gradient)" : "rgba(255,255,255,0.16)"}
              strokeWidth={trusted ? 3 : 1.25}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={
                active
                  ? { pathLength: 1, opacity: trusted ? 0.9 : 1 }
                  : { pathLength: 0, opacity: 0 }
              }
              transition={{
                delay: active ? EDGE_BASE_DELAY + i * EDGE_STAGGER : 0,
                duration: EDGE_DURATION,
                ease: "easeOut",
              }}
            />
          );
        })}
      </svg>

      {/* Orbiting nodes */}
      {NODES.map((n, i) => {
        const trusted = n.kind === "trusted";
        const Icon = n.icon;
        const nodeColor = n.x < 50 ? from : to;
        const popDelay = EDGE_BASE_DELAY + i * EDGE_STAGGER + EDGE_DURATION * 0.55;
        return (
          <div
            key={n.label}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${n.x}%`, top: `${n.y}%` }}
          >
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={active ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
              transition={
                active
                  ? { delay: popDelay, type: "spring", stiffness: 260, damping: 20 }
                  : { duration: 0.25 }
              }
            >
              <motion.div
                animate={active ? { y: [0, -7, 0] } : { y: 0 }}
                transition={
                  active
                    ? {
                        delay: popDelay + 0.6,
                        duration: 4 + i * 0.45,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }
                    : { duration: 0.3 }
                }
              >
                <div className="relative">
                  {trusted ? (
                    <div
                      className="flex items-center justify-center rounded-full border backdrop-blur-sm"
                      style={{
                        width: n.size,
                        height: n.size,
                        borderColor: `${nodeColor}66`,
                        background: `radial-gradient(circle at 35% 30%, ${nodeColor}2e, rgba(11,14,26,0.85))`,
                        boxShadow: `0 0 36px -6px ${nodeColor}59`,
                      }}
                    >
                      {Icon && (
                        <Icon
                          strokeWidth={1.75}
                          style={{
                            width: n.size * 0.4,
                            height: n.size * 0.4,
                            color: nodeColor,
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    <div
                      className="rounded-full border border-white/15 bg-white/5"
                      style={{
                        width: n.size,
                        height: n.size,
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
                      }}
                    />
                  )}
                  <div
                    className={`absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap font-mono text-xl ${
                      trusted ? "text-white/90" : "text-white/45"
                    }`}
                  >
                    {n.label}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        );
      })}

      {/* Central hub */}
      <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
        <motion.div
          className="relative"
          initial={{ scale: 0.6, opacity: 0, filter: "blur(6px)" }}
          animate={
            active
              ? { scale: 1, opacity: 1, filter: "blur(0px)" }
              : { scale: 0.6, opacity: 0, filter: "blur(6px)" }
          }
          transition={{ delay: active ? 0.15 : 0, duration: 0.6, ease: EASE }}
        >
          <motion.div
            className="pointer-events-none absolute left-1/2 top-1/2 -z-10 rounded-full"
            style={{
              width: 260,
              height: 260,
              x: "-50%",
              y: "-50%",
              background: `radial-gradient(circle, ${from}40 0%, ${to}1a 45%, transparent 70%)`,
              filter: "blur(14px)",
            }}
            animate={active ? { scale: [1, 1.16, 1], opacity: [0.7, 1, 0.7] } : { opacity: 0.4 }}
            transition={
              active
                ? { duration: 4.5, repeat: Infinity, ease: "easeInOut" }
                : { duration: 0.3 }
            }
          />
          <div
            className="flex items-center gap-3 rounded-full border bg-ink-900/85 px-7 py-4 backdrop-blur-xl"
            style={{
              borderColor: `${from}80`,
              boxShadow: `0 0 40px -4px ${from}55, 0 0 72px -12px ${to}55`,
            }}
          >
            <Link2 className="h-7 w-7 shrink-0" style={{ color: from }} strokeWidth={2} />
            <span className="font-mono text-2xl font-medium text-white">yoursite.example</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
